/**
 * Đo thời gian từng bước của một màn nặng, bật/tắt tại chỗ không cần build lại.
 *
 * Vì sao không dùng thẳng console.time: màn sửa đợt thu có gần chục bước nối nhau (2 API,
 * 4 vòng lặp trên vài nghìn dòng, 1 lần setFieldsValue của antd). console.time in ra từng
 * dòng rời rạc, không nhìn ra bước nào chiếm bao nhiêu phần của tổng. Ở đây gom lại một
 * bảng có cột cộng dồn, và tự chỉ ra bước chậm nhất.
 *
 * Bật trên production: localStorage.setItem("onepay.perf", "1") rồi F5. Tắt: xoá key đó.
 * Môi trường dev luôn bật.
 */

const FLAG_KEY = "onepay.perf";

const readFlag = () => {
    try {
        return localStorage.getItem(FLAG_KEY) === "1";
    } catch (e) {
        // Trình duyệt chặn storage (cửa sổ ẩn danh) — coi như tắt, không để ném ra ngoài
        return false;
    }
};

export const isTraceOn = () => readFlag() || process.env.NODE_ENV === "development";

// Trace rỗng để nơi gọi không phải bọc if quanh từng mốc.
const NOOP_TRACE = {
    mark: () => {},
    markPaint: () => Promise.resolve(),
    end: () => {},
};

/**
 * @param {string} label tên màn/luồng, hiện ở tiêu đề nhóm log
 */
export function createTrace(label) {
    if (!isTraceOn()) return NOOP_TRACE;

    const startedAt = performance.now();
    let lastAt = startedAt;
    const rows = [];

    const push = (name, note) => {
        const now = performance.now();
        rows.push({
            "Bước": name,
            "Mất (ms)": Math.round(now - lastAt),
            "Cộng dồn (ms)": Math.round(now - startedAt),
            "Ghi chú": note == null ? "" : String(note),
        });
        lastAt = now;
    };

    return {
        mark(name, note) {
            push(name, note);
            // Để mốc hiện luôn trên tab Performance của DevTools, khỏi phải dò bằng mắt
            try {
                performance.mark(`${label}: ${name}`);
            } catch (e) {
                /* không quan trọng */
            }
        },

        /**
         * Mốc "người dùng thật sự nhìn thấy": đợi React commit xong rồi trình duyệt vẽ
         * xong khung hình kế tiếp. rAF lồng hai lần là cách đo gần nhất với lúc pixel
         * lên màn hình.
         */
        markPaint(name, note) {
            return new Promise((resolve) => {
                // Tab ở nền thì trình duyệt ngưng requestAnimationFrame. Không có hàng rào
                // này thì promise treo vĩnh viễn và cả bảng đo không bao giờ được in.
                let xong = false;
                const ketThuc = (ghiChu) => {
                    if (xong) return;
                    xong = true;
                    push(name, ghiChu);
                    resolve();
                };

                setTimeout(() => ketThuc("không đo được (tab ở nền)"), 5000);

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => ketThuc(note));
                });
            });
        },

        end(note) {
            if (rows.length === 0) return;   // không có mốc nào thì khỏi in bảng rỗng
            const total = Math.round(performance.now() - startedAt);
            const slowest = rows.reduce(
                (a, b) => (a && a["Mất (ms)"] >= b["Mất (ms)"] ? a : b),
                rows[0]
            );

            console.group(`⏱ ${label} — tổng ${total} ms${note ? ` · ${note}` : ""}`);
            console.table(rows);
            if (slowest) {
                const phan = total > 0 ? Math.round((slowest["Mất (ms)"] / total) * 100) : 0;
                console.log(`Chậm nhất: ${slowest["Bước"]} — ${slowest["Mất (ms)"]} ms (${phan}% tổng)`);
            }
            console.groupEnd();
        },
    };
}

/**
 * Bọc quanh một lời gọi async để biết riêng nó tốn bao lâu. Dùng cho các API chạy song
 * song ngoài luồng chính, không nằm trong bảng của createTrace.
 */
export async function timeAsync(label, fn) {
    if (!isTraceOn()) return fn();
    const t0 = performance.now();
    try {
        return await fn();
    } finally {
        console.log(`⏱ ${label}: ${Math.round(performance.now() - t0)} ms`);
    }
}
